/**
 * CAPTION IT — Briefing v4 LEAN
 *
 * Filosofia:
 *  - 3 step (era 7): età+occasione+vibe / chi è / materiale extra
 *  - Niente AI-talk per il cliente: gamification puro ("apri il pack")
 *  - Copy adattivo: i titoli/lead degli step cambiano in base a età+occasione
 *  - Reveal stile FIFA UT (in arrivo: cascade carte generali → 5x10 SS "hai pescato")
 *  - Edit finale sempre disponibile
 *
 * Stato attuale: SCAFFOLD MINIMALE — solo 3 step + autosave + nav.
 * Composizione carte + reveal: prossime PR.
 */
(function () {
  'use strict';

  const AUTOSAVE_KEY_PREFIX = 'ci-brief-v4:';
  // Fallback endpoints se la section setting nel theme è vuota.
  // Vercel app URL fissa (vedi remix-app/shopify.app.toml application_url).
  const DEFAULT_AI_ENDPOINT = 'https://shopify-memes-411b.vercel.app/api/cards/generate';
  const DEFAULT_SUBMIT_ENDPOINT = 'https://shopify-memes-411b.vercel.app/api/briefings/confirm';

  function $(sel, root) { return (root || document).querySelector(sel); }

  /* ── Compressione lato client ──────────────────────────────────────────
     Vercel accetta max ~4.5MB per richiesta, quindi il server rifiuta > 4MB.
     Le foto da smartphone sono spesso 5-12MB: le ridimensioniamo (lato max
     2000px) e ricodifichiamo in JPEG prima dell'upload. Se il browser non
     riesce a decodificare il file (es. HEIC su Android) mandiamo l'originale. */
  const MAX_DIM = 2000;
  const TARGET_BYTES = Math.round(3.2 * 1024 * 1024);
  function compressImage(file) {
    var isImage = /^image\//i.test(file.type) || /\.(heic|heif|jpe?g|png|webp)$/i.test(file.name || '');
    if (!isImage) return Promise.resolve(file);
    var smallEnough = file.size <= TARGET_BYTES && /^image\/(jpeg|jpg|png|webp)$/i.test(file.type);
    if (smallEnough) return Promise.resolve(file);
    if (typeof document === 'undefined' || !document.createElement('canvas').getContext) return Promise.resolve(file);
    return new Promise(function (resolve) {
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        try {
          URL.revokeObjectURL(url);
          var w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
          if (!w || !h) { resolve(file); return; }
          var scale = Math.min(1, MAX_DIM / Math.max(w, h));
          var canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(w * scale));
          canvas.height = Math.max(1, Math.round(h * scale));
          var ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          var q = 0.86;
          var attempt = function () {
            canvas.toBlob(function (blob) {
              if (!blob) { resolve(file); return; }
              if (blob.size > TARGET_BYTES && q > 0.5) { q -= 0.1; attempt(); return; }
              var name = (file.name || 'foto').replace(/\.[^.]+$/, '') + '.jpg';
              try { resolve(new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() })); }
              catch (e) { blob.name = name; resolve(blob); }
            }, 'image/jpeg', q);
          };
          attempt();
        } catch (e) { resolve(file); }
      };
      img.onerror = function () { URL.revokeObjectURL(url); resolve(file); };
      img.src = url;
    });
  }

  /* Messaggi comprensibili per il cliente a partire dalla risposta API */
  function friendlyUploadError(status, d, fileName) {
    var code = (d && (d.error || '')) || '';
    if (status === 403 || status === 404 || code === 'unauthorized' || code === 'briefing_not_found') {
      return 'Non troviamo ancora il tuo ordine nel nostro sistema. Subito dopo l’acquisto può volerci qualche minuto: riprova tra poco usando lo stesso link. Se il problema continua, rispondi all’email dell’ordine e lo sistemiamo noi.';
    }
    if (status === 413 || code === 'file_too_large') return 'La foto "' + fileName + '" è troppo pesante (max 4MB). Prova con un’altra foto o uno screenshot.';
    if (status === 415 || code === 'invalid_mime') return 'Formato di "' + fileName + '" non supportato: usa JPG, PNG o WEBP.';
    if (status === 409 || code === 'photo_limit_reached') return 'Hai già caricato il massimo di 25 foto.';
    if (status === 502 || code === 'blob_upload_failed' || code === 'blob_not_configured') return 'Problema temporaneo sul nostro archivio foto. Riprova tra qualche minuto; se continua, scrivici rispondendo all’email dell’ordine.';
    return 'Upload "' + fileName + '" fallito: ' + ((d && (d.message || d.error)) || ('errore ' + status));
  }

  function $$(sel, root) { return Array.from((root || document).querySelectorAll(sel)); }
  function debounce(fn, ms) {
    let t;
    return function () {
      clearTimeout(t);
      const args = arguments, ctx = this;
      t = setTimeout(() => fn.apply(ctx, args), ms);
    };
  }
  function readParams() {
    const u = new URL(window.location.href);
    const get = (k) => u.searchParams.get(k) || '';
    return {
      order: get('order') || '—',
      token: get('token') || '—',
      line: get('line') || '',
      honoree: get('honoree') || '',
      occasion: get('occasion') || '',
      pkg: get('pkg') || '',
      // v4-extra: dati dal questionario pre-acquisto (line item properties Shopify)
      age: get('age') || '',
      vibe: get('vibe') || '',
      lore: get('lore') || '',
      extraStory: get('extraStory') || get('aneddoto') || '',
      // Cast: amico_1=Marco|BFF, amico_2=Luca|fratello ecc (separatore "|")
      amico_1: get('amico_1') || get('amico1') || '',
      amico_2: get('amico_2') || get('amico2') || '',
      amico_3: get('amico_3') || get('amico3') || '',
    };
  }

  /* Mapping occasione: il pre-acquisto manda "compleanno"/"laurea"/etc,
     il post potrebbe ricevere anche "Compleanno" maiuscolo (Shopify cart property).
     Normalizza in lowercase per matching coi chip. */
  function normalizeOccasion(s) {
    const map = {
      'compleanno': 'compleanno',
      'laurea': 'laurea',
      'matrimonio': 'matrimonio',
      'addio celibato': 'addio',
      'addio nubilato': 'addio',
      'addio celibato/nubilato': 'addio',
      'addio': 'addio',
      'altro': 'altro',
    };
    const k = String(s || '').trim().toLowerCase();
    return map[k] || k;
  }

  /* ──────────────────────────────────────────────────────────────────────
   * Pool filtering selettivo (età × vibe × occasione)
   * ──────────────────────────────────────────────────────────────────────
   * Il merchant ha chiesto: se uno è anziano non sa i meme dei giovani,
   * se uno è "spicy" gli piacciono battute più dark. Etc.
   *
   * Computa 2 score:
   *   - maturity (0-100): 0=Gen Z, 100=Boomer. Combina età + occasione
   *   - spice (0-100):    0=safe, 100=spinto. Combina vibe + occasione
   *
   * Poi filtra il pool escludendo carte che contengono parole chiave
   * incompatibili col contesto (es. "TikTok" se maturity > 70, "scopare"
   * se spice < 50).
   */
  const GEN_Z_KEYWORDS = [
    'tiktok', 'brainrot', 'chatgpt', 'tinder', 'bumble', 'onlyfans',
    'reels', 'reel', 'story instagram', 'screenshot', 'scroll',
    'mood', 'vibe', 'cringe', 'pov', 'aesthetic', 'main character',
    'red flag', 'ick', 'beige flag', 'rizz', 'situationship', 'ghosting',
    'spid', 'whatsapp', 'discord', 'twitch',
  ];
  const ADULT_KEYWORDS = [
    'scopare', 'scopata', 'scopa', 'sesso', 'sessuale', 'sex',
    'tradire', 'tradisce', 'tradito', 'tradita', 'ex',
    'tette', 'culo', 'cazzo', 'figa', 'pene', 'vagina',
    'sborra', 'sega', 'pompino', 'troia', 'puttana', 'pornhub',
    'vibratore', 'preservativo', 'sesso anale', 'orgia',
    'gravidanza', 'incinta', 'aborto', 'depilare', 'depili',
    'squirt', 'fetish',
  ];
  const MATURE_KEYWORDS = [
    'mutuo', 'suocera', 'suocero', 'pannolini', 'mensa scolastica',
    'pensione', 'colonscopia', 'dentista', 'glicemia', 'colesterolo',
    'condominio', 'casello', 'autovelox',
  ];

  function computeMaturity(age, occasion) {
    const baseAge = { under25: 20, '25-35': 40, '35-50': 70, '50plus': 90 }[age] || 50;
    const occMod = { matrimonio: 15, laurea: -5, addio: -10, compleanno: 0, altro: 0 }[occasion] || 0;
    return Math.max(0, Math.min(100, baseAge + occMod));
  }

  function computeSpice(vibe, occasion) {
    const baseVibe = { irriverente: 30, chaos: 55, spicy: 90 }[vibe] || 50;
    const occMod = { addio: 20, matrimonio: -20, laurea: -10, compleanno: 0, altro: 0 }[occasion] || 0;
    return Math.max(0, Math.min(100, baseVibe + occMod));
  }

  function containsAny(text, keywords) {
    const lower = String(text || '').toLowerCase();
    return keywords.some((k) => lower.includes(k));
  }

  function filterPoolByContext(cards, maturity, spice) {
    return cards.filter((c) => {
      // Maturity alta (>70) → escludi riferimenti Gen Z / app moderne
      if (maturity > 70 && containsAny(c, GEN_Z_KEYWORDS)) return false;
      // Maturity bassa (<35) → escludi riferimenti maturi (suocera, mutuo, etc)
      if (maturity < 35 && containsAny(c, MATURE_KEYWORDS)) return false;
      // Spice basso (<50) → escludi sessuale esplicito
      if (spice < 50 && containsAny(c, ADULT_KEYWORDS)) return false;
      return true;
    });
  }

  /* Copy adattivo: titoli/lead degli step cambiano in base a età+occasione.
     L'AI nel briefing v3 era troppo "neutro". v4: tono che cambia con il
     contesto scelto dal cliente.
     Esempi:
       <25 addio celibato → "Costruiamo il gioco più cringe della serata"
       50+ pranzo famiglia → "Ok andiamoci leggeri, ma con sostanza"
  */
  function adaptiveCopy(state) {
    const age = state.age || '';
    const occ = state.occasion || '';
    const vibe = state.vibe || '';
    const spicy = vibe === 'spicy' || (vibe === 'chaos' && (age === 'under25' || age === '25-35'));

    // STEP 1: nessun input ancora, copy iniziale
    const step1Title = 'partiamo dalle <span class="ci-highlight">basi</span>';
    const step1Lead = '3 cose veloci che danno il tono al tuo mazzo.';

    // STEP 2: dopo le scelte di step 1
    let step2Title = 'ora dimmi chi è';
    let step2Lead = 'Più dettagli, più le carte diventano spaventosamente specifiche.';
    if (occ === 'addio') step2Title = 'parlami del <span class="ci-highlight ci-highlight--pink">disastro</span>';
    else if (occ === 'matrimonio') step2Title = 'parlami degli <span class="ci-highlight">sposi</span>';
    else if (occ === 'laurea') step2Title = 'parlami del/della <span class="ci-highlight">dottore</span>';
    if (spicy) step2Lead = 'Non risparmiamo nessuno. Spara dettagli.';

    // STEP 3
    let step3Title = 'ultima: <span class="ci-highlight ci-highlight--pink">storie vere</span>?';
    let step3Lead = 'Tre caselle. Tutte opzionali. Servono SOLO per le carte più potenti. Se hai voglia, sblocca tutto.';
    if (spicy) step3Lead = 'Tre caselle. Una bomba ognuna. Se hai roba pesante, qui è oro.';
    else if (age === '50plus') step3Lead = 'Tre caselle. Tutte opzionali. Se hai un aneddoto carino, qui dentro.';

    return {
      step1: { title: step1Title, lead: step1Lead },
      step2: { title: step2Title, lead: step2Lead },
      step3: { title: step3Title, lead: step3Lead },
    };
  }

  class BriefingV4 {
    constructor(root) {
      this.root = root;
      this.aiEndpoint = root.dataset.aiEndpoint || DEFAULT_AI_ENDPOINT;
      this.submitEndpoint = root.dataset.submitEndpoint || DEFAULT_SUBMIT_ENDPOINT;
      // Safety: se la section setting passa una URL relativa o malformata, fallback assoluto.
      if (!/^https?:\/\//i.test(this.aiEndpoint)) this.aiEndpoint = DEFAULT_AI_ENDPOINT;
      if (!/^https?:\/\//i.test(this.submitEndpoint)) this.submitEndpoint = DEFAULT_SUBMIT_ENDPOINT;
      this.params = readParams();
      // Carica state da localStorage se esiste, altrimenti pre-popola da URL params
      // (caso pre-acquisto → post: cliente ha già compilato il quiz, dati portati nel link)
      const fromUrlFriends = [];
      for (const k of ['amico_1', 'amico_2', 'amico_3']) {
        const v = this.params[k];
        if (v) {
          // Formato accettato: "Nome|Ruolo" oppure solo "Nome"
          const parts = v.split('|').map((s) => s.trim());
          fromUrlFriends.push({ name: parts[0] || '', role: parts[1] || '' });
        }
      }
      this.state = this.loadState() || {
        // Step 1 — pre-popolato da URL params se presente
        age: this.params.age || '',
        occasion: normalizeOccasion(this.params.occasion),
        vibe: this.params.vibe || '',
        // Step 2
        honoree: this.params.honoree || root.dataset.honoreeDefault || '',
        friends: fromUrlFriends,
        lore: this.params.lore || '',
        // Step 3
        extraStory: this.params.extraStory || '',
        extraQuote: '',
        extraScene: '',
      };
      this.step = 1;
      this.totalSteps = 4; // 1: tono · 2: chi è · 3: storie · 4: foto
      this.photos = []; // [{url, name, size, uploadedAt}]

      this.cacheEls();
      this.populateHeader();
      this.bind();
      this.applyState();
      this.refreshAdaptiveCopy();
      this._injectFormCSS();
      this.renderStep();
      this._resumeDeckIfAny();
      this._checkServerReset();
    }

    /* Reset da admin (app Remix → Briefing → Gestisci): se il backend ha un
       resetAt diverso da quello visto da questo browser, ripulisce deck/sent
       (e le risposte se resetMode = 'quiz') e ricarica la pagina. */
    async _checkServerReset() {
      if (!this.params.token || this.params.token === '—' || !this.params.line) return;
      const apiBase = this.aiEndpoint.replace(/\/api\/cards\/generate.*$/, '');
      try {
        const r = await fetch(`${apiBase}/api/photos/list`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'list', cartToken: this.params.token, lineItemId: this.params.line }),
        });
        const d = await r.json().catch(() => null);
        if (!d || !d.ok || !d.resetAt) return;
        const key = this.keyFor();
        let seen = null;
        let hadLocal = false;
        try {
          seen = localStorage.getItem(key + ':resetAt');
          if (seen === d.resetAt) return;
          hadLocal = !!(localStorage.getItem(key + ':deck') || localStorage.getItem(key + ':sent') || localStorage.getItem(key));
          localStorage.setItem(key + ':resetAt', d.resetAt);
          localStorage.removeItem(key + ':deck');
          localStorage.removeItem(key + ':sent');
          if (d.resetMode === 'quiz') localStorage.removeItem(key);
        } catch (_e) { return; }
        if (hadLocal) window.location.reload();
      } catch (_e) { /* network optional */ }
    }

    /* Se in localStorage c'è già un deck salvato (cliente ha aperto il pack e
       poi ricaricato la pagina), riprendiamo direttamente dalla vista edit
       invece di rifargli rifare il questionario. Protegge dalla perdita dati
       su refresh. Se già inviato in stampa, mostra la conferma. */
    _resumeDeckIfAny() {
      let savedDeck = null;
      try {
        const raw = localStorage.getItem(this.keyFor() + ':deck');
        if (raw) savedDeck = JSON.parse(raw);
      } catch (_e) { savedDeck = null; }
      if (!Array.isArray(savedDeck) || savedDeck.length === 0) return;
      this.deck = savedDeck;
      this.ssCards = this.deck.filter((c) => c.tier === 'situation_ship');
      this.genCards = this.deck.filter((c) => c.tier === 'general');
      const nav = $('.ci-brief__nav', this.root);
      if (nav) nav.style.display = 'none';
      const body = $('.ci-brief__body', this.root);
      if (body) body.style.display = 'none';
      const progress = $('.ci-brief__progress', this.root);
      if (progress) progress.style.display = 'none';
      const reveal = $('[data-brief-reveal]', this.root);
      if (reveal) reveal.hidden = false;
      this._injectRevealCSS();
      let alreadySent = false;
      try { alreadySent = localStorage.getItem(this.keyFor() + ':sent') === '1'; } catch (_e) { /* */ }
      if (alreadySent) {
        this._renderPrintSuccess();
      } else {
        this._renderEditMode();
      }
    }

    /* CSS dedicato per il QUESTIONARIO (chip, input, textarea, labels).
       Iniettato al boot — non solo al reveal. Senza, gli elementi appaiono
       come default browser (tristi). Conservativo: non stravolge layout. */
    _injectFormCSS() {
      if (document.getElementById('ci-brief-v4-form-css')) return;
      const s = document.createElement('style');
      s.id = 'ci-brief-v4-form-css';
      s.textContent = `
        /* Chip selections (età / occasione / vibe) */
        .ci-brief-v4 .ci-brief__chip {
          display: inline-flex; align-items: center;
          padding: 10px 18px; margin: 0 8px 8px 0;
          background: #FFFFFF; color: #281C5E;
          border: 2px solid #281C5E; border-radius: 999px;
          font-family: 'Bricolage Grotesque', system-ui, sans-serif;
          font-weight: 700; font-size: 14px; letter-spacing: -0.01em;
          cursor: pointer; transition: all 0.2s ease;
          line-height: 1; user-select: none;
        }
        .ci-brief-v4 .ci-brief__chip:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(40,28,94,0.18);
          background: #FEF9EF;
        }
        .ci-brief-v4 .ci-brief__chip--active {
          background: #281C5E !important; color: #C8E420 !important;
          border-color: #281C5E !important;
          transform: scale(1.05); box-shadow: 0 4px 14px rgba(40,28,94,0.35);
        }
        .ci-brief-v4 .ci-brief__chips {
          display: flex; flex-wrap: wrap; gap: 0; margin-bottom: 8px;
        }

        /* Labels (H3) */
        .ci-brief-v4 .ci-brief__field-title {
          font-family: 'Bricolage Grotesque', system-ui, sans-serif;
          font-weight: 800; font-size: 15px; text-transform: uppercase;
          letter-spacing: 0.8px; color: #281C5E;
          margin: 28px 0 6px;
        }

        /* Input + textarea */
        .ci-brief-v4 .ci-brief__input,
        .ci-brief-v4 .ci-brief__textarea {
          display: block; width: 100%;
          padding: 14px 16px;
          background: #FFFFFF; color: #281C5E;
          border: 2px solid rgba(40,28,94,0.25); border-radius: 12px;
          font-family: 'Bricolage Grotesque', system-ui, sans-serif;
          font-weight: 500; font-size: 15px; line-height: 1.4;
          transition: border-color 0.2s, box-shadow 0.2s;
          box-sizing: border-box;
        }
        .ci-brief-v4 .ci-brief__textarea {
          resize: vertical; min-height: 80px;
        }
        .ci-brief-v4 .ci-brief__input::placeholder,
        .ci-brief-v4 .ci-brief__textarea::placeholder {
          color: rgba(40,28,94,0.45); font-weight: 400;
        }
        .ci-brief-v4 .ci-brief__input:focus,
        .ci-brief-v4 .ci-brief__textarea:focus {
          outline: none; border-color: #281C5E;
          box-shadow: 0 0 0 4px rgba(200,228,32,0.30);
        }

        /* Friend list (cast cards) */
        .ci-brief-v4 .ci-brief__friends-grid {
          display: flex; flex-direction: column; gap: 8px;
          margin: 12px 0;
        }
        .ci-brief-v4 .ci-brief__friend {
          background: #FFFFFF;
          border: 2px solid #281C5E; border-radius: 10px;
          padding: 12px 14px;
          font-family: 'Bricolage Grotesque', system-ui, sans-serif;
          font-size: 14px; color: #281C5E;
        }

        /* Add button (cast) */
        .ci-brief-v4 .ci-brief__add {
          background: transparent; color: #281C5E;
          border: 2px dashed #281C5E; border-radius: 999px;
          padding: 10px 20px; font-weight: 700; font-size: 13px;
          cursor: pointer; transition: all 0.2s;
          font-family: 'Bricolage Grotesque', system-ui, sans-serif;
        }
        .ci-brief-v4 .ci-brief__add:hover {
          background: #281C5E; color: #FEF9EF;
        }

        /* Lead text (descrizioni sotto i title) */
        .ci-brief-v4 .ci-brief__lead--small {
          font-size: 13px; color: rgba(40,28,94,0.65);
          margin: -2px 0 8px; line-height: 1.4;
        }

        /* Spacing migliorato per il body step */
        .ci-brief-v4 .ci-brief__step {
          padding: 24px 4px;
        }

        /* Footer nav — sticky bottom + transition */
        .ci-brief-v4 .ci-brief__nav {
          background: rgba(254,249,239,0.95);
          backdrop-filter: blur(8px);
          border-top: 2px solid rgba(40,28,94,0.12);
          padding: 14px 16px;
        }
        .ci-brief-v4 .ci-brief__nav-inner {
          display: flex; align-items: center; justify-content: space-between;
          gap: 12px; max-width: 900px; margin: 0 auto;
        }
        .ci-brief-v4 .ci-brief__nav-step {
          font-family: 'Bricolage Grotesque', monospace;
          font-size: 12px; color: rgba(40,28,94,0.55);
          letter-spacing: 1px; text-transform: uppercase;
        }
        .ci-brief-v4 .ci-brief__btn {
          font-family: 'Bricolage Grotesque', system-ui, sans-serif;
          font-weight: 800; font-size: 14px;
          padding: 14px 24px; border-radius: 999px;
          border: 2px solid #281C5E;
          cursor: pointer; transition: all 0.2s;
          letter-spacing: 0.3px;
        }
        .ci-brief-v4 .ci-brief__btn:disabled {
          opacity: 0.35; cursor: not-allowed;
        }
        .ci-brief-v4 .ci-brief__btn--prev {
          background: transparent; color: #281C5E;
        }
        .ci-brief-v4 .ci-brief__btn--prev:hover:not(:disabled) {
          background: #281C5E; color: #FEF9EF;
        }
        .ci-brief-v4 .ci-brief__btn--next {
          background: #281C5E; color: #FEF9EF;
        }
        .ci-brief-v4 .ci-brief__btn--next:hover {
          background: #C8E420; color: #281C5E; border-color: #C8E420;
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(200,228,32,0.45);
        }
        .ci-brief-v4 .ci-brief__btn--generate {
          background: linear-gradient(135deg, #C8E420 0%, #f06b8c 100%);
          color: #281C5E; border-color: transparent;
          font-size: 15px; padding: 16px 28px;
          box-shadow: 0 6px 20px rgba(240,107,140,0.35);
        }
        .ci-brief-v4 .ci-brief__btn--generate:hover {
          transform: translateY(-3px) scale(1.02);
          box-shadow: 0 8px 24px rgba(240,107,140,0.5);
        }
      `;
      document.head.appendChild(s);
    }

    cacheEls() {
      const r = this.root;
      this.elStepNum = $('[data-brief-step-num]', r);
      this.elStepNumF = $('[data-brief-step-num-footer]', r);
      this.elProgFill = $('[data-brief-progress-fill]', r);
      this.elPrev = $('[data-brief-prev]', r);
      this.elNext = $('[data-brief-next]', r);
      this.elSubmit = $('[data-brief-submit]', r);
      this.elToast = $('[data-brief-toast]', r);
      this.steps = $$('.ci-brief__step', r);

      this.elStep1Title = $('[data-brief-step1-title]', r);
      this.elStep1Lead = $('[data-brief-step1-lead]', r);
      this.elStep2Title = $('[data-brief-step2-title]', r);
      this.elStep2Lead = $('[data-brief-step2-lead]', r);
      this.elStep3Title = $('[data-brief-step3-title]', r);
      this.elStep3Lead = $('[data-brief-step3-lead]', r);
    }

    populateHeader() {
      const set = (sel, val) => { const el = $(sel, this.root); if (el) el.textContent = val; };
      set('[data-brief-order]', this.params.order);
      set('[data-brief-pkg]', this.params.pkg || 'MEDIUM');
    }

    bind() {
      this.elPrev?.addEventListener('click', () => this.go(this.step - 1));
      this.elNext?.addEventListener('click', () => this.go(this.step + 1));
      this.elSubmit?.addEventListener('click', () => this.openPack());

      // Step 1 — chip selections (single-choice each)
      $$('[data-brief-age]', this.root).forEach((btn) => {
        btn.addEventListener('click', () => { this.state.age = btn.dataset.briefAge; this.refreshChipActive('age'); this.refreshAdaptiveCopy(); this.persistDebounced(); });
      });
      $$('[data-brief-occasion]', this.root).forEach((btn) => {
        btn.addEventListener('click', () => { this.state.occasion = btn.dataset.briefOccasion; this.refreshChipActive('occasion'); this.refreshAdaptiveCopy(); this.persistDebounced(); });
      });
      $$('[data-brief-vibe]', this.root).forEach((btn) => {
        btn.addEventListener('click', () => { this.state.vibe = btn.dataset.briefVibe; this.refreshChipActive('vibe'); this.refreshAdaptiveCopy(); this.persistDebounced(); });
      });

      // Step 2 — friends list + simple inputs
      const friendName = $('[data-brief-input="friend-name"]', this.root);
      const friendRole = $('[data-brief-input="friend-role"]', this.root);
      const addFriend = $('[data-brief-add="friends"]', this.root);
      addFriend?.addEventListener('click', () => {
        const n = (friendName?.value || '').trim();
        const r = (friendRole?.value || '').trim();
        if (!n || this.state.friends.length >= 3) return;
        this.state.friends.push({ name: n, role: r });
        if (friendName) friendName.value = '';
        if (friendRole) friendRole.value = '';
        this.renderFriends();
        this.persist();
      });

      // Generic single-input bindings
      $$('[data-brief-input]', this.root).forEach((input) => {
        const k = input.dataset.briefInput;
        if (['friend-name', 'friend-role'].includes(k)) return;
        input.addEventListener('input', () => {
          this.state[k] = input.value;
          this.persistDebounced();
        });
      });

      this.persistDebounced = debounce(() => this.persist(), 600);

      // Photo upload (step 4) — bind input file + dropzone + load existing
      this._bindPhotoUpload();
    }

    /* ---------- PHOTO UPLOAD (step 4) ---------- */
    _bindPhotoUpload() {
      const dropzone = $('[data-brief-photo-dropzone]', this.root);
      const input = $('[data-brief-photo-input]', this.root);
      if (!dropzone || !input) return;

      // Click sull'area apre il file picker
      dropzone.addEventListener('click', () => input.click());
      // Selezione file
      input.addEventListener('change', (e) => {
        const files = Array.from(e.target.files || []);
        this._uploadPhotos(files);
        e.target.value = ''; // reset per ri-selezionare lo stesso file
      });
      // Drag & drop
      dropzone.addEventListener('dragover', (e) => {
        e.preventDefault();
        dropzone.classList.add('ci-brief__photo-dropzone--dragover');
      });
      dropzone.addEventListener('dragleave', () => {
        dropzone.classList.remove('ci-brief__photo-dropzone--dragover');
      });
      dropzone.addEventListener('drop', (e) => {
        e.preventDefault();
        dropzone.classList.remove('ci-brief__photo-dropzone--dragover');
        const files = Array.from(e.dataTransfer?.files || []);
        this._uploadPhotos(files);
      });

      this._injectPhotoCSS();
      // Carica foto già esistenti dal server (se cliente ricarica la pagina)
      this._loadExistingPhotos();
    }

    _injectPhotoCSS() {
      if (document.getElementById('ci-brief-v4-photo-css')) return;
      const s = document.createElement('style');
      s.id = 'ci-brief-v4-photo-css';
      s.textContent = `
        .ci-brief__photo-dropzone { border: 2px dashed #281C5E; border-radius: 14px; padding: 32px 20px; text-align: center; background: rgba(40,28,94,0.04); cursor: pointer; transition: all 0.2s; }
        .ci-brief__photo-dropzone:hover { background: rgba(40,28,94,0.08); }
        .ci-brief__photo-dropzone--dragover { background: rgba(200,228,32,0.20); border-color: #C8E420; border-style: solid; }
        .ci-brief__photo-thumb { position: relative; aspect-ratio: 1; border-radius: 10px; overflow: hidden; background: #281C5E; }
        .ci-brief__photo-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .ci-brief__photo-thumb-remove { position: absolute; top: 6px; right: 6px; width: 24px; height: 24px; border-radius: 50%; background: rgba(40,28,94,0.85); color: #FEF9EF; border: none; cursor: pointer; font-size: 14px; line-height: 1; display: flex; align-items: center; justify-content: center; transition: background 0.15s; }
        .ci-brief__photo-thumb-remove:hover { background: #c8233b; }
        .ci-brief__photo-thumb--uploading { opacity: 0.5; }
        .ci-brief__photo-thumb--uploading::after { content: '…'; position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; color: #FEF9EF; font-size: 24px; }
      `;
      document.head.appendChild(s);
    }

    async _loadExistingPhotos() {
      // Endpoint /api/photos/list (su Vercel app, stesso base)
      if (!this.params.token || this.params.token === '—' || !this.params.line) return;
      const apiBase = this.aiEndpoint.replace(/\/api\/cards\/generate.*$/, '');
      try {
        const r = await fetch(`${apiBase}/api/photos/list`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'list', cartToken: this.params.token, lineItemId: this.params.line }),
        });
        const d = await r.json().catch(() => null);
        if (d?.ok && Array.isArray(d.photos)) {
          this.photos = d.photos;
          this._renderPhotos();
        }
      } catch (_e) { /* ignora, network optional */ }
    }

    async _uploadPhotos(files) {
      const apiBase = this.aiEndpoint.replace(/\/api\/cards\/generate.*$/, '');
      const grid = $('[data-brief-photo-grid]', this.root);

      for (const file of files) {
        if (this.photos.length >= 25) {
          alert('Hai raggiunto il limite di 25 foto.');
          break;
        }
        // Placeholder thumb durante upload
        const placeholderId = `pup-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        const placeholder = document.createElement('div');
        placeholder.className = 'ci-brief__photo-thumb ci-brief__photo-thumb--uploading';
        placeholder.id = placeholderId;
        // Mostra preview locale immediato via FileReader
        const reader = new FileReader();
        reader.onload = (e) => { placeholder.innerHTML = `<img src="${e.target.result}" alt="">`; };
        reader.readAsDataURL(file);
        grid?.appendChild(placeholder);

        // Compressione lato client (foto smartphone 5-12MB → JPEG ≤ ~3MB)
        const prepared = await compressImage(file);
        if (prepared.size > 4 * 1024 * 1024) {
          placeholder.remove();
          alert(`La foto "${file.name}" è troppo pesante anche dopo la compressione (max 4MB). Prova con un'altra foto.`);
          continue;
        }

        const fd = new FormData();
        fd.append('file', prepared, prepared.name || file.name);
        fd.append('cartToken', this.params.token);
        fd.append('lineItemId', this.params.line);

        try {
          const r = await fetch(`${apiBase}/api/photos/upload`, { method: 'POST', body: fd });
          const d = await r.json().catch(() => null);
          if (!r.ok || !d?.ok) {
            placeholder.remove();
            alert(friendlyUploadError(r.status, d, file.name));
            continue;
          }
          this.photos.push({ url: d.url, name: file.name, size: prepared.size, uploadedAt: new Date().toISOString() });
          placeholder.remove();
        } catch (e) {
          placeholder.remove();
          alert(`Errore di rete su "${file.name}": ${e?.message || ''} — controlla la connessione e riprova.`);
        }
      }
      this._renderPhotos();
    }

    _renderPhotos() {
      const grid = $('[data-brief-photo-grid]', this.root);
      const counter = $('[data-brief-photo-counter]', this.root);
      if (counter) counter.textContent = `${this.photos.length} / 25 foto caricate`;
      if (!grid) return;
      grid.innerHTML = '';
      this.photos.forEach((p) => {
        const div = document.createElement('div');
        div.className = 'ci-brief__photo-thumb';
        div.innerHTML = `<img src="${p.url}" alt="${(p.name || '').replace(/"/g, '&quot;')}">
          <button type="button" class="ci-brief__photo-thumb-remove" aria-label="Rimuovi" data-photo-url="${p.url}">✕</button>`;
        div.querySelector('.ci-brief__photo-thumb-remove').addEventListener('click', () => this._deletePhoto(p.url));
        grid.appendChild(div);
      });
    }

    async _deletePhoto(url) {
      const apiBase = this.aiEndpoint.replace(/\/api\/cards\/generate.*$/, '');
      try {
        const r = await fetch(`${apiBase}/api/photos/list`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'delete', cartToken: this.params.token, lineItemId: this.params.line, url }),
        });
        const d = await r.json().catch(() => null);
        if (r.ok && d?.ok) {
          this.photos = this.photos.filter((p) => p.url !== url);
          this._renderPhotos();
        }
      } catch (e) {
        alert('Errore eliminazione foto: ' + (e?.message || ''));
      }
    }

    /* Render adaptive copy based on age+occasion+vibe */
    refreshAdaptiveCopy() {
      const c = adaptiveCopy(this.state);
      if (this.elStep1Title) this.elStep1Title.innerHTML = c.step1.title;
      if (this.elStep1Lead)  this.elStep1Lead.textContent = c.step1.lead;
      if (this.elStep2Title) this.elStep2Title.innerHTML = c.step2.title;
      if (this.elStep2Lead)  this.elStep2Lead.textContent = c.step2.lead;
      if (this.elStep3Title) this.elStep3Title.innerHTML = c.step3.title;
      if (this.elStep3Lead)  this.elStep3Lead.textContent = c.step3.lead;
    }

    refreshChipActive(field) {
      const value = this.state[field];
      const attr = `data-brief-${field}`;
      $$(`[${attr}]`, this.root).forEach((btn) => {
        const isActive = btn.getAttribute(attr) === value;
        btn.classList.toggle('ci-brief__chip--active', isActive);
      });
    }

    applyState() {
      // Reflect state back into form (re-renderable after reload)
      this.refreshChipActive('age');
      this.refreshChipActive('occasion');
      this.refreshChipActive('vibe');
      $$('[data-brief-input]', this.root).forEach((input) => {
        const k = input.dataset.briefInput;
        if (['friend-name', 'friend-role'].includes(k)) return;
        if (typeof this.state[k] === 'string') input.value = this.state[k];
      });
      this.renderFriends();
    }

    renderFriends() {
      const grid = $('[data-brief-list="friends"]', this.root);
      if (!grid) return;
      grid.innerHTML = '';
      this.state.friends.forEach((f, i) => {
        const div = document.createElement('div');
        div.className = 'ci-brief__friend';
        div.innerHTML = `<strong>${f.name}</strong> <span style="opacity:.7">· ${f.role || '—'}</span> <button type="button" aria-label="Rimuovi" style="background:none;border:none;color:inherit;cursor:pointer;opacity:.6;float:right">✕</button>`;
        div.querySelector('button').addEventListener('click', () => {
          this.state.friends.splice(i, 1);
          this.renderFriends();
          this.persist();
        });
        grid.appendChild(div);
      });
    }

    go(n) {
      if (n < 1) n = 1;
      if (n > this.totalSteps) n = this.totalSteps;
      this.step = n;
      this.renderStep();
    }

    renderStep() {
      this.steps.forEach((s) => {
        const isActive = Number(s.getAttribute('data-step')) === this.step;
        s.hidden = !isActive;
        s.classList.toggle('ci-brief__step--active', isActive);
      });
      if (this.elStepNum) this.elStepNum.textContent = String(this.step);
      if (this.elStepNumF) this.elStepNumF.textContent = String(this.step);
      if (this.elProgFill) this.elProgFill.style.width = `${(this.step / this.totalSteps) * 100}%`;
      if (this.elPrev) this.elPrev.disabled = this.step === 1;
      const isLast = this.step === this.totalSteps;
      if (this.elNext) {
        this.elNext.hidden = isLast;
        // Footer copy adattivo per ogni step (gamification)
        const nextLabels = { 1: 'andiamo →', 2: 'perfetto, vai →', 3: 'ora le foto →' };
        this.elNext.textContent = nextLabels[this.step] || 'avanti →';
      }
      if (this.elSubmit) {
        this.elSubmit.hidden = !isLast;
        // Copy adattivo CTA finale in base alla vibe (rinforza il tono scelto)
        const vibe = this.state.vibe;
        const submitLabels = {
          irriverente: '🎴 mostrami le carte',
          chaos: '🔥 apri il pack ora',
          spicy: '💀 sgancia la bomba',
        };
        this.elSubmit.textContent = submitLabels[vibe] || '🎴 apri il pack';
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    /* Apre il pack: chiama backend per 50 SS + pesca 50 Generali dal pool */
    async openPack() {
      if (!this.state.honoree || !this.state.age || !this.state.occasion || !this.state.vibe) {
        alert('Compila almeno età, occasione, vibe e nome festeggiato.');
        return;
      }
      this.showRevealLoading();

      // Mapping vibe v4 → TOV pool (3 buckets serio/educato/spinto)
      const TOV_MAP = { irriverente: 'educato', chaos: 'educato', spicy: 'spinto' };
      const tov = TOV_MAP[this.state.vibe] || 'educato';

      // Pool selection FRONTEND: 75 generali (senza marker [NOME]) filtrate per età/vibe/occasione
      const pool = (window.MEMANDOCON_POOL && window.MEMANDOCON_POOL.base && window.MEMANDOCON_POOL.expansion) ? window.MEMANDOCON_POOL : null;
      let generals = [];
      if (pool) {
        const allTOV = [...(pool.base[tov] || []), ...(pool.expansion[tov] || [])];
        const noName = allTOV.filter((c) => !/\[NOME\]/.test(c));
        // Filtro selettivo basato su maturity (età × occasione) + spice (vibe × occasione)
        const maturity = computeMaturity(this.state.age, this.state.occasion);
        const spice = computeSpice(this.state.vibe, this.state.occasion);
        const filtered = filterPoolByContext(noName, maturity, spice);
        console.log('[v4.pool]', { tov, maturity, spice, total: noName.length, afterFilter: filtered.length });
        // Se il filtro lascia meno di 75 carte, fallback al pool completo (no carte = no mazzo)
        generals = this._sampleFromArray(filtered.length >= 75 ? filtered : noName, 75);
      }

      // BACKEND call: 25 Situation Ship (carte iper-personalizzate, max 25 — il resto è pool)
      let ssCards = [];
      try {
        const payload = {
          cartToken: this.params.token,
          lineItemId: this.params.line,
          briefing: {
            mode: 'compose-v4',
            targetCount: 25,
            v4: {
              honoree: this.state.honoree,
              age: this.state.age,
              occasion: this.state.occasion,
              vibe: this.state.vibe,
              friends: this.state.friends,
              lore: this.state.lore,
              extraStory: this.state.extraStory,
              extraQuote: this.state.extraQuote,
              extraScene: this.state.extraScene,
            },
            excludeTexts: generals,
            // Campi compat per backend che richiede honoree/etc top-level
            honoree: this.state.honoree,
            occasion: this.state.occasion,
          },
        };
        const r = await fetch(this.aiEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const d = await r.json().catch(() => null);
        if (!r.ok || !d || !Array.isArray(d.cards)) {
          const msg = (d && (d.message || d.error)) || ('errore ' + r.status);
          this.showRevealError(msg);
          return;
        }
        ssCards = d.cards.map((c, i) => ({ id: c.id || `ss-${i}`, text: c.text, tier: 'situation_ship', confidence: c.confidence }));
      } catch (e) {
        this.showRevealError('errore di rete: ' + (e?.message || ''));
        return;
      }

      // Compose 100 — MIX random: SS sparpagliate tra le generali (no più 2 fasi separate)
      const generalsTier = generals.map((text, i) => ({ id: `g-${i}`, text, tier: 'general' }));
      const mixed = [...ssCards, ...generalsTier];
      // Padding fino a 100 se mancano carte (AI ha generato meno, pool filtrato troppo, etc)
      // Pesca extra dal pool noName (anche fuori filtro per evitare deck monco)
      if (mixed.length < 100 && pool) {
        const allTOV = [...(pool.base[tov] || []), ...(pool.expansion[tov] || [])];
        const allNoName = allTOV.filter((c) => !/\[NOME\]/.test(c));
        const existingTexts = new Set(mixed.map((c) => c.text));
        const available = allNoName.filter((t) => !existingTexts.has(t));
        const needed = 100 - mixed.length;
        const extras = this._sampleFromArray(available, needed);
        extras.forEach((text, i) => mixed.push({ id: `g-pad-${i}`, text, tier: 'general' }));
      }
      // Hard pad finale se ancora < 100 (pool esaurito): testo fallback
      while (mixed.length < 100) {
        mixed.push({ id: `g-fb-${mixed.length}`, text: `Quando ${(this.state.honoree || 'lui').toUpperCase()} fa quella sua cosa e tutti scoppiamo a ridere`, tier: 'general' });
      }
      // Shuffle Fisher-Yates per spalmare le SS in mezzo
      for (let i = mixed.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [mixed[i], mixed[j]] = [mixed[j], mixed[i]];
      }
      this.deck = mixed;
      this.showRevealDeck();
    }

    _sampleFromArray(arr, n) {
      if (!Array.isArray(arr) || arr.length === 0 || n <= 0) return [];
      const a = arr.slice();
      const out = [];
      const take = Math.min(n, a.length);
      for (let i = 0; i < take; i++) {
        const idx = Math.floor(Math.random() * (a.length - i));
        out.push(a[idx]);
        a[idx] = a[a.length - 1 - i];
      }
      return out;
    }

    /* ---------- Reveal UI (stub semplice, FIFA UT arriva in PR successiva) ---------- */
    showRevealLoading() {
      const r = $('[data-brief-reveal]', this.root);
      if (!r) return;
      r.hidden = false;
      r.innerHTML = `<div style="padding: 60px 24px; text-align: center;">
        <div style="font-family: var(--ci-font-display, system-ui); font-weight: 900; font-size: 28px; color: var(--ci-ink); margin-bottom: 12px;">apertura del pack…</div>
        <div style="font-size: 14px; opacity: 0.6;">qualche secondo, vale la pena.</div>
      </div>`;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    showRevealError(msg) {
      const r = $('[data-brief-reveal]', this.root);
      if (!r) return;
      r.innerHTML = `<div style="padding: 60px 24px; text-align: center;">
        <div style="font-family: var(--ci-font-display, system-ui); font-weight: 900; font-size: 24px; color: #C8233B; margin-bottom: 12px;">qualcosa è andato storto</div>
        <div style="font-size: 14px; opacity: 0.7;">${msg}</div>
        <button type="button" id="reveal-retry" style="margin-top: 18px; padding: 12px 24px; background: var(--ci-ink); color: var(--ci-paper); border: none; border-radius: 8px; font-weight: 800; cursor: pointer;">riprova</button>
      </div>`;
      document.getElementById('reveal-retry')?.addEventListener('click', () => this.openPack());
    }
    /* ---------- REVEAL FIFA UT-style (single mixed cascade + edit) ---------- */
    showRevealDeck() {
      const r = $('[data-brief-reveal]', this.root);
      if (!r) return;
      this.ssCards = this.deck.filter((c) => c.tier === 'situation_ship');
      this.genCards = this.deck.filter((c) => c.tier === 'general');
      // Backup deck in localStorage: se il cliente ricarica la pagina non perde
      // le carte generate (vedi _resumeDeckIfAny nel costruttore).
      try { localStorage.setItem(this.keyFor() + ':deck', JSON.stringify(this.deck)); } catch (_e) { /* ignora */ }

      // Nascondi footer nav originale (indietro/avanti/apri-pack) — non più rilevante
      // dopo che il pack è aperto. Il prossimo step (edit/conferma) ha CTA proprio
      // nella vista reveal/edit.
      const nav = $('.ci-brief__nav', this.root);
      if (nav) nav.style.display = 'none';
      // Nascondi anche il body del questionario (i 3 step)
      const body = $('.ci-brief__body', this.root);
      if (body) body.style.display = 'none';
      // Nascondi progress bar (è di "passo X di 3", ormai è già tutto fatto)
      const progress = $('.ci-brief__progress', this.root);
      if (progress) progress.style.display = 'none';

      // Inject CSS animations + style carte (una volta sola)
      this._injectRevealCSS();

      // Cascade UNICO mixato (SS sparpagliate tra Generali)
      this._renderRevealCascade();
    }

    _injectRevealCSS() {
      if (document.getElementById('ci-reveal-css')) return;
      const s = document.createElement('style');
      s.id = 'ci-reveal-css';
      s.textContent = `
        .ci-rv__stage { position: relative; min-height: 90vh; background: #FAEED8; padding: 24px 16px 80px; overflow: hidden; }
        .ci-rv__title { font-family: var(--ci-font-display, system-ui); font-weight: 900; font-size: clamp(24px, 4vw, 38px); color: #281C5E; margin: 0 0 8px; text-align: center; }
        .ci-rv__sub { font-family: var(--ci-font-display, system-ui); font-size: 14px; opacity: 0.7; text-align: center; margin: 0 0 28px; color: #281C5E; }
        .ci-rv__board { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 10px; max-width: 1200px; margin: 0 auto; }
        .ci-rv__card { background: #5546A0; color: #FFFFFF; border-radius: 12px; padding: 18px 14px; min-height: 200px; display: flex; flex-direction: column; justify-content: space-between; font-family: var(--ci-font-display, system-ui); font-weight: 600; font-size: 13px; line-height: 1.35; opacity: 0; transform: translateY(20px) scale(0.95); animation: ci-rv-card-in 0.45s ease-out forwards; box-shadow: 0 4px 12px rgba(85,70,155,0.25); position: relative; }
        .ci-rv__card-text { flex: 1; padding-top: 2px; }
        .ci-rv__card-text::first-letter { border-bottom: 2px solid #FFFFFF; padding-bottom: 1px; }
        .ci-rv__card-footer { font-family: var(--ci-font-mono, monospace); font-size: 9px; letter-spacing: 1.2px; text-align: right; opacity: 0.7; margin-top: 12px; }
        .ci-rv__card--ss { background: #5546A0; }
        .ci-rv__card--ss .ci-rv__ss-pretitle { font-family: var(--ci-font-mono, monospace); font-size: 8px; letter-spacing: 1.5px; text-transform: uppercase; opacity: 0.7; margin-bottom: 4px; }
        .ci-rv__card--ss .ci-rv__ss-pill { display: inline-block; background: #C8E420; color: #281C5E; padding: 3px 9px; border-radius: 999px; font-weight: 800; font-size: 10px; letter-spacing: 0.8px; text-transform: uppercase; margin-bottom: 14px; }
        @keyframes ci-rv-card-in { to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes ci-rv-ss-pop { 0% { opacity: 0; transform: scale(0.5) rotate(-8deg); } 60% { opacity: 1; transform: scale(1.05) rotate(2deg); } 100% { opacity: 1; transform: scale(1) rotate(0); } }
        .ci-rv__card--ss-revealing { animation: ci-rv-ss-pop 0.6s ease-out forwards; }
        .ci-rv__phase-banner { background: #281C5E; color: #FAEED8; padding: 28px 20px; border-radius: 18px; text-align: center; max-width: 600px; margin: 0 auto 24px; }
        .ci-rv__phase-banner-eyebrow { font-family: var(--ci-font-mono, monospace); font-size: 11px; letter-spacing: 2px; text-transform: uppercase; opacity: 0.7; margin-bottom: 6px; }
        .ci-rv__phase-banner-title { font-family: var(--ci-font-display, system-ui); font-weight: 900; font-size: clamp(20px, 3vw, 30px); margin: 0 0 12px; line-height: 1.1; }
        .ci-rv__phase-banner-sub { font-size: 13px; opacity: 0.8; }
        .ci-rv__cta { display: block; margin: 28px auto 0; background: #C8E420; color: #281C5E; border: none; padding: 16px 32px; border-radius: 999px; font-family: var(--ci-font-display, system-ui); font-weight: 900; font-size: 16px; letter-spacing: 0.5px; cursor: pointer; box-shadow: 0 6px 16px rgba(0,0,0,0.15); transition: transform 0.15s; }
        .ci-rv__cta:hover { transform: translateY(-2px); }
        .ci-rv__sticky-counter { position: fixed; top: 12px; right: 12px; background: rgba(40,28,94,0.92); color: #FAEED8; padding: 6px 12px; border-radius: 999px; font-family: var(--ci-font-mono, monospace); font-size: 12px; font-weight: 700; letter-spacing: 1px; z-index: 100; }
        .ci-rv__card--editable { transition: transform 0.2s, box-shadow 0.2s; }
        .ci-rv__card--editable:hover { transform: translateY(-3px) scale(1.02); box-shadow: 0 8px 20px rgba(85,70,155,0.4); }
        .ci-rv__card--editable::after { content: '✎'; position: absolute; top: 8px; right: 10px; font-size: 14px; opacity: 0.5; }
        /* UI questionario fluida — transizioni smooth tra step */
        .ci-brief-v4 .ci-brief__step { transition: opacity 0.3s ease, transform 0.3s ease; }
        .ci-brief-v4 .ci-brief__step[hidden] { display: none !important; }
        .ci-brief-v4 .ci-brief__step--active { animation: ci-step-in 0.35s ease-out; }
        @keyframes ci-step-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .ci-brief-v4 .ci-brief__chip { transition: all 0.2s ease; cursor: pointer; }
        .ci-brief-v4 .ci-brief__chip:hover { transform: translateY(-1px); }
        .ci-brief-v4 .ci-brief__chip--active { background: #281C5E !important; color: #FAEED8 !important; border-color: #281C5E !important; transform: scale(1.05); box-shadow: 0 4px 12px rgba(40,28,94,0.3); }
        .ci-brief-v4 .ci-brief__btn { transition: all 0.2s ease; }
        .ci-brief-v4 .ci-brief__btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(0,0,0,0.15); }
        .ci-brief-v4 .ci-brief__input, .ci-brief-v4 .ci-brief__textarea { transition: border-color 0.2s ease, box-shadow 0.2s ease; }
        .ci-brief-v4 .ci-brief__input:focus, .ci-brief-v4 .ci-brief__textarea:focus { outline: none; border-color: #281C5E; box-shadow: 0 0 0 3px rgba(40,28,94,0.12); }
      `;
      document.head.appendChild(s);
    }

    /* CASCADE UNICO — 100 carte mixate (SS sparpagliate tra Generali) */
    _renderRevealCascade() {
      const r = $('[data-brief-reveal]', this.root);
      const honoreeUpper = (this.state.honoree || '').toUpperCase();
      r.innerHTML = `<div class="ci-rv__stage">
        <h2 class="ci-rv__title">il tuo pack è pronto</h2>
        <p class="ci-rv__sub">${this.deck.length} carte · ${this.ssCards.length} ${honoreeUpper} situationship sparpagliate dentro · cerca i bollini lime 🔥</p>
        <div class="ci-rv__sticky-counter" id="rv-counter">0 / ${this.deck.length}</div>
        <div class="ci-rv__board" id="rv-board"></div>
        <button type="button" class="ci-rv__cta" id="rv-next-edit" style="display:none;">ora modifica le carte →</button>
      </div>`;
      window.scrollTo({ top: 0, behavior: 'smooth' });

      const board = document.getElementById('rv-board');
      const counter = document.getElementById('rv-counter');
      let revealed = 0;
      // Cascade 100 carte con stagger 40ms → ~4s totali
      this.deck.forEach((c, i) => {
        setTimeout(() => {
          const type = c.tier === 'situation_ship' ? 'ss' : 'general';
          const card = this._buildCardElement(c, type, i);
          if (type === 'ss') card.classList.add('ci-rv__card--ss-revealing');
          board.appendChild(card);
          revealed++;
          if (counter) counter.textContent = `${revealed} / ${this.deck.length}`;
        }, i * 40);
      });

      // CTA "edit" appare a fine cascade
      setTimeout(() => {
        const btn = document.getElementById('rv-next-edit');
        if (btn) {
          btn.style.display = 'block';
          btn.addEventListener('click', () => this._renderEditMode());
        }
      }, this.deck.length * 40 + 500);
    }

    /* EDIT MODE — Cliente può modificare ogni carta tramite tap */
    _renderEditMode() {
      const r = $('[data-brief-reveal]', this.root);
      const honoreeUpper = (this.state.honoree || '').toUpperCase();
      r.innerHTML = `<div class="ci-rv__stage">
        <h2 class="ci-rv__title">modifica le carte</h2>
        <p class="ci-rv__sub">tap su una carta per cambiarne il testo · ${this.ssCards.length} ${honoreeUpper} situationship + ${this.genCards.length} generali</p>
        <div class="ci-rv__board" id="rv-board-edit"></div>
        <button type="button" class="ci-rv__cta" id="rv-send-print">📦 invia in stampa</button>
        <p style="text-align:center; margin: 12px 0 0; font-size: 11px; opacity: 0.55;">una volta inviato le carte vengono salvate e non potrai più modificarle</p>
      </div>`;
      const board = document.getElementById('rv-board-edit');
      this.deck.forEach((c, i) => {
        const card = this._buildCardElement(c, c.tier === 'situation_ship' ? 'ss' : 'general', i, true);
        card.style.cursor = 'pointer';
        card.addEventListener('click', (e) => {
          // Evita di re-aprire se già in edit
          if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'BUTTON') return;
          this._enterCardEdit(card, c, i);
        });
        board.appendChild(card);
      });
      const sendBtn = document.getElementById('rv-send-print');
      if (sendBtn) sendBtn.addEventListener('click', () => this._sendToPrint(sendBtn));
    }

    /* INVIA IN STAMPA — persiste le 100 carte sul gestionale (Neon) e lock-in.
       Due step server-side, entrambi già esistenti:
        1) POST /api/cards/generate { finalSave:true, cards:[...] } → salva il
           deck nel DB, Briefing.status = BRIEF_DONE
        2) POST /api/briefings/confirm { cartToken, lineItemId } → lock-in,
           Briefing.status = CARDS_APPROVED ("inviato in stampa" lato cliente).
       Da quel momento l'ufficio vede le carte in admin e procede alla stampa. */
    async _sendToPrint(btn) {
      if (!this.params.token || this.params.token === '—' || !this.params.line) {
        this.showToast('errore: link senza ordine, contatta il negozio');
        return;
      }
      if (!Array.isArray(this.deck) || this.deck.length === 0) {
        this.showToast('errore: nessuna carta da inviare');
        return;
      }
      const orig = btn.textContent;
      btn.disabled = true;
      btn.style.opacity = '0.6';
      btn.style.cursor = 'wait';
      btn.textContent = 'invio in corso…';
      const texts = this.deck
        .map((c) => String(c && c.text ? c.text : '').trim())
        .filter((t) => t.length > 0)
        .slice(0, 100);
      try {
        // 1) Salva le carte sul gestionale (Neon)
        const saveRes = await fetch(this.aiEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cartToken: this.params.token,
            lineItemId: this.params.line,
            briefing: {
              finalSave: true,
              cards: texts,
              honoree: this.state.honoree,
              occasion: this.state.occasion,
            },
          }),
        });
        const saveData = await saveRes.json().catch(() => null);
        if (!saveRes.ok || !saveData || !saveData.ok) {
          throw new Error((saveData && (saveData.message || saveData.error)) || ('salvataggio fallito (' + saveRes.status + ')'));
        }
        // 2) Lock-in finale → CARDS_APPROVED
        const confirmRes = await fetch(this.submitEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cartToken: this.params.token, lineItemId: this.params.line }),
        });
        const confirmData = await confirmRes.json().catch(() => null);
        if (!confirmRes.ok || !confirmData || !confirmData.ok) {
          throw new Error((confirmData && (confirmData.message || confirmData.error)) || ('conferma fallita (' + confirmRes.status + ')'));
        }
        try {
          localStorage.setItem(this.keyFor() + ':sent', '1');
          localStorage.setItem(this.keyFor() + ':deck', JSON.stringify(this.deck));
        } catch (_e) { /* ignora */ }
        this._renderPrintSuccess();
      } catch (e) {
        btn.disabled = false;
        btn.style.opacity = '1';
        btn.style.cursor = 'pointer';
        btn.textContent = orig;
        this.showToast('errore invio: ' + (e && e.message ? e.message : 'riprova'));
      }
    }

    _renderPrintSuccess() {
      const r = $('[data-brief-reveal]', this.root);
      if (!r) return;
      r.innerHTML = `<div class="ci-rv__stage" style="text-align:center; display:flex; flex-direction:column; align-items:center; justify-content:center;">
        <h2 class="ci-rv__title">🎉 inviato in stampa!</h2>
        <p class="ci-rv__sub" style="max-width:480px;">le tue ${this.deck.length} carte sono state salvate. Ci pensiamo noi alla stampa — riceverai un aggiornamento via email. Puoi chiudere questa pagina.</p>
      </div>`;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    /* Inline edit di una singola carta */
    _enterCardEdit(cardEl, cardData, idx) {
      const textEl = cardEl.querySelector('.ci-rv__card-text');
      if (!textEl || textEl.dataset.editing === '1') return;
      textEl.dataset.editing = '1';
      const currentText = cardData.text;
      textEl.innerHTML = `
        <textarea style="width:100%;height:80px;background:rgba(255,255,255,0.92);color:#281C5E;border:none;border-radius:8px;padding:8px;font-family:inherit;font-size:13px;line-height:1.4;resize:none;">${currentText.replace(/</g, '&lt;')}</textarea>
        <div style="display:flex;gap:6px;margin-top:8px;">
          <button type="button" data-action="save" style="flex:1;background:#C8E420;color:#281C5E;border:none;padding:6px 10px;border-radius:6px;font-weight:800;font-size:11px;cursor:pointer;">salva</button>
          <button type="button" data-action="cancel" style="flex:1;background:rgba(255,255,255,0.2);color:#FFFFFF;border:none;padding:6px 10px;border-radius:6px;font-weight:600;font-size:11px;cursor:pointer;">annulla</button>
        </div>`;
      const ta = textEl.querySelector('textarea');
      ta.focus();
      ta.select();
      textEl.querySelector('[data-action="save"]').addEventListener('click', () => {
        const newText = ta.value.trim() || currentText;
        cardData.text = newText;
        this.deck[idx].text = newText;
        textEl.dataset.editing = '';
        textEl.innerHTML = newText;
        // Persist in localStorage
        try { localStorage.setItem(this.keyFor() + ':deck', JSON.stringify(this.deck)); } catch (_e) {}
      });
      textEl.querySelector('[data-action="cancel"]').addEventListener('click', () => {
        textEl.dataset.editing = '';
        textEl.innerHTML = currentText;
      });
    }

    /* Builder carta singola — replica del design dei screenshot del merchant */
    _buildCardElement(c, type, idx, clickable) {
      const honoreeUpper = (this.state.honoree || '').toUpperCase();
      const div = document.createElement('div');
      div.className = `ci-rv__card ${type === 'ss' ? 'ci-rv__card--ss' : ''} ${clickable ? 'ci-rv__card--editable' : ''}`;
      if (typeof idx === 'number') div.dataset.cardIdx = String(idx);
      const safeText = String(c.text || '').replace(/</g, '&lt;');
      if (type === 'ss') {
        div.innerHTML = `
          <div>
            <div class="ci-rv__ss-pretitle">Oh yes! Hai pescato una:</div>
            <span class="ci-rv__ss-pill">${honoreeUpper} situationship</span>
          </div>
          <div class="ci-rv__card-text">${safeText}</div>
          <div class="ci-rv__card-footer">memando®</div>`;
      } else {
        div.innerHTML = `
          <div class="ci-rv__card-text">${safeText}</div>
          <div class="ci-rv__card-footer">memando®</div>`;
      }
      return div;
    }

    /* ---------- Persistence (localStorage only per ora) ---------- */
    keyFor() {
      return `${AUTOSAVE_KEY_PREFIX}${this.params.order}_${this.params.line}`;
    }
    persist() {
      try {
        localStorage.setItem(this.keyFor(), JSON.stringify(this.state));
        this.showToast('salvato');
      } catch (_e) { /* ignora */ }
    }
    loadState() {
      try {
        const raw = localStorage.getItem(this.keyFor());
        return raw ? JSON.parse(raw) : null;
      } catch (_e) { return null; }
    }
    showToast(msg) {
      if (!this.elToast) return;
      this.elToast.textContent = msg;
      this.elToast.classList.add('ci-brief__toast--visible');
      clearTimeout(this._toastT);
      this._toastT = setTimeout(() => this.elToast.classList.remove('ci-brief__toast--visible'), 1200);
    }
  }

  /* Bootstrap */
  document.addEventListener('DOMContentLoaded', () => {
    const root = document.querySelector('[data-ci-brief-v4]');
    if (!root) return;
    new BriefingV4(root);
  });
})();